import { useQuery } from '@tanstack/react-query';

// Interfaz para las especialidades según la respuesta de la API
interface ApiSpecialty {
  ESPECIALIDAD_CODIGO: string;
  ESPECIALIDAD_NOMBRE: string;
}

// Interfaz para las especialidades normalizadas
interface Specialty {
  ESPECIALIDAD: string;
  CODIGO: string;
}

// Mapeo de códigos según buscarPorOpciones
const SEARCH_TYPE_CODES: Record<string, string> = {
  'Especialidad médica': 'C1',
  'Especialidad odontológica': 'C8',
  'Nombre del profesional': 'C1',
  'Salud mental': 'C1',
  'Atención de urgencia': 'C13',
  'Atención sanatorial': 'C5',
  'Estudio por imágenes': 'C10',
  'Laboratorios': 'C2',
  'Farmacias': 'C6',
  'Ópticas': 'C12'
};

interface UseSpecialtiesParams {
  plan: string;
  searchType: string;
  locationData?: {
    TIPOLOC: string;
    ZONA: string;
    USERLOCATION: string;
    PROVINCIA: string;
    SYSTEMLOCATION: string;
  };
}

export function useSpecialties({ plan, searchType, locationData }: UseSpecialtiesParams) {
  return useQuery({
    queryKey: ['specialties', plan, searchType, locationData],
    queryFn: async () => {
      // Validar que tengamos los datos necesarios
      if (!plan || !searchType) {
        return [];
      }

      const searchCode = SEARCH_TYPE_CODES[searchType];
      
      if (!searchCode) {
        throw new Error('Tipo de búsqueda no válido');
      }

      // Construir parámetros de ubicación según TIPOLOC, con el mismo criterio que useSearch.
      // El backend mapea: zona→zCodigo, userLocation→localidad, provincia→provincia, systemLocation→barrio.
      // Importante:
      //  - trimear los valores (vienen rellenados con espacios CHAR; si no, el backend da 404)
      //  - usar SYSTEMLOCATION como localidad/barrio, NUNCA USERLOCATION (que es texto de display)
      //  - el endpoint de especialidades matchea el barrio/localidad SENSIBLE A MAYÚSCULAS.
      //    Las localidades ya vienen en mayúsculas (MORON), pero los barrios de CABA vienen
      //    en mixed-case (Palermo, Belgrano) y devolvían 0 especialidades. Por eso pasamos
      //    SYSTEMLOCATION en mayúsculas (VEN-394). El endpoint de búsqueda NO es sensible.
      const buildLocationParams = () => {
        const base = { zona: '=', userLocation: '=', provincia: '=', systemLocation: '=' };
        if (!locationData) return base;

        const zona = locationData.ZONA?.trim() || '=';
        const provincia = locationData.PROVINCIA?.trim() || '=';
        const systemLocation = locationData.SYSTEMLOCATION?.trim().toUpperCase() || '=';

        const tipoloc = locationData.TIPOLOC?.trim().toUpperCase() || '';

        switch (tipoloc) {
          case 'ZONA':
            return { ...base, zona };
          case 'PROVINCIA':
            return { ...base, provincia };
          case 'LOCALIDAD':
            return { ...base, zona, provincia, userLocation: systemLocation };
          case 'BARRIO':
            return { ...base, zona, provincia, systemLocation };
          default:
            if (zona !== '=' && provincia === '=') {
              return { ...base, zona };
            }
            return { zona, provincia, userLocation: systemLocation, systemLocation };
        }
      };

      const params = new URLSearchParams({
        plan,
        searchCode,
        ...buildLocationParams(),
      });

      const url = `/api/specialties?${params.toString()}`;

      const res = await fetch(url);

      if (!res.ok) throw new Error('Error al obtener las especialidades');
      const data = await res.json();

      // Obtener el array de especialidades de la respuesta
      const specialtiesArray = Array.isArray(data.ESPECIALIDAD) ? data.ESPECIALIDAD : [data.ESPECIALIDAD];
      
      // Mapear la estructura de la API a la estructura esperada por el componente
      const mappedSpecialties = specialtiesArray
        .filter((specialty: ApiSpecialty) => specialty && specialty.ESPECIALIDAD_NOMBRE && specialty.ESPECIALIDAD_CODIGO)
        .map((specialty: ApiSpecialty): Specialty => ({
          ESPECIALIDAD: specialty.ESPECIALIDAD_NOMBRE,
          CODIGO: specialty.ESPECIALIDAD_CODIGO
        }));

      // VEN-413: el backend devuelve "Laboratorio(s)" dentro del catálogo de
      // Atención sanatorial (C5), pero Laboratorios es su propio tipo de
      // búsqueda (C2) y no corresponde como especialidad sanatorial.
      if (searchType === 'Atención sanatorial') {
        return mappedSpecialties.filter(
          (specialty: Specialty) => !/laborator/i.test(specialty.ESPECIALIDAD)
        );
      }

      return mappedSpecialties;
    },
    enabled: !!plan && !!searchType,
    staleTime: 1000 * 60 * 5, // 5 minutos
    gcTime: 1000 * 60 * 10, // 10 minutos
  });
} 